import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AppComponent } from '../app.component';
import { ProfileService } from '../services/profile.service';
import { VoxPopService } from '../services/voxpop.service';
import { Programme } from '../types/programme';

@Component({
  selector: 'programme-detail',
  templateUrl: './programme-detail.component.html',
  styleUrls: ['./programme-detail.component.css'],
  providers: [VoxPopService],
  standalone: false
})
export class ProgrammeDetailComponent implements OnInit {
  @ViewChild('editButton') editButton: ElementRef<HTMLButtonElement>;
  @ViewChild('nameInput') nameInput: ElementRef<HTMLInputElement>;
  @Input() inserting: any = false;
  @Output() cancelled = new EventEmitter<any>();
  programme: Programme;
  programmeDraft: Programme;
  editable = false;
  saving = false;
  errorMessage = '';
  saveMessage = '';

  constructor(private voxPopService: VoxPopService, private route: ActivatedRoute) { }

  ngOnInit(): void {
    if (!ProfileService.IsLoggedIn()) { return; }
    if (this.inserting) { this.programme = new Programme(); this.edit(); }
    else { this.route.params.subscribe(() => this.loadProgramme()); }
  }

  loadProgramme(): void {
    this.errorMessage = '';
    this.voxPopService.getProgramme(+this.route.snapshot.params['programmeid']).subscribe(
      programme => this.setProgramme(programme),
      () => this.errorMessage = 'Could not load programme details. Please retry.',
    );
  }

  setProgramme(programme: Programme): void {
    this.programme = programme;
    AppComponent.SetTitle((programme.name || 'Programme') + ' (' + programme.id + ')');
  }

  CanAdminister(): boolean {
    return this.programme?.id > 0 ? ProfileService.CanAdministerProgramme(this.programme.id) : ProfileService.CanAdminister();
  }
  GlobalAdmin(): boolean { return ProfileService.CanAdministerAll(); }

  forceValidShortName(value: string, input: HTMLInputElement): void {
    // Preserve the existing short-name normalization.
    this.programmeDraft.short_name = value.toLowerCase().replace(/[^a-z0-9_-]/g, '');
    input.value = this.programmeDraft.short_name;
  }

  edit(): void {
    this.programmeDraft = Object.assign(new Programme(), this.programme);
    this.editable = true;
    this.errorMessage = this.saveMessage = '';
    setTimeout(() => this.nameInput?.nativeElement.focus());
  }

  cancel(): void {
    if (this.saving) { return; }
    this.editable = false;
    this.programmeDraft = null;
    this.errorMessage = '';
    this.cancelled.emit(null);
    setTimeout(() => this.editButton?.nativeElement.focus());
  }

  save(): void {
    if (this.saving || !this.programmeDraft || !this.CanAdminister()) { return; }
    this.saving = true;
    this.errorMessage = '';
    const payload = Object.assign(new Programme(), this.programmeDraft);
    if (<any>payload.active === 'true') { payload.active = true; }
    if (<any>payload.active === 'false') { payload.active = false; }
    const request = this.inserting ? this.voxPopService.createProgramme(payload) : this.voxPopService.updateProgramme(payload);
    request.subscribe(
      saved => {
        // The update response is a status envelope: re-read the server representation.
        this.voxPopService.getProgramme(saved.id).subscribe(
          programme => {
            this.setProgramme(programme);
            this.saving = this.editable = this.inserting = false;
            this.programmeDraft = null;
            this.saveMessage = 'Programme saved.';
            setTimeout(() => this.editButton?.nativeElement.focus());
          },
          () => {
            this.saving = false;
            this.errorMessage = 'Programme saved, but details could not be refreshed. Reload to see the saved values. Your draft is still here.';
          },
        );
      },
      () => { this.saving = false; this.errorMessage = 'Could not save programme. Your changes are still here. Please try Save again.'; },
    );
  }
}
